import { currencyAtom } from "@/atoms/currencyAtom"
import { Card, Descriptions } from "antd"
import { useTranslation } from "react-i18next"
import React from "react"
import { useRecoilValue } from "recoil"
import DisplayPrice from "@/UI/DataDisplay/DisplayPrice"
import { userAtom } from "@/atoms/userAtom"
import { PriceConfiguration } from "@/types/drivesAppSettings"
import { DistanceUnit } from "@/types/localization.types"
import { faPen } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import DriverPayDeleteModal from "../DriverPayDeleteModal/DriverPayDeleteModal"
import DriverPayFormModal from "../DriverPayFormModal/DriverPayFormModal.container"

const IconContainer: React.FC<{ children: React.ReactNode; onClick: () => void }> = ({
  children,
  onClick,
}) => {
  return (
    <div
      className="flex cursor-pointer item-center bg-[#F3F4F6] rounded-full p-2"
      onClick={onClick}
    >
      {children}
    </div>
  )
}

export interface DriverPayCardProps {
  config: PriceConfiguration
  type?: "teams" | "clients"
}

const DriverPayCard: React.FC<DriverPayCardProps> = ({ config, type = "teams" }) => {
  const { t } = useTranslation()

  const currency = useRecoilValue(currencyAtom)
  const { preference } = useRecoilValue(userAtom)

  const { basePrice, perDelivery, perKm, perMi, minKm, minMi, teams, clients } = config

  const teamNames = teams?.map((team) => team.name).join(", ")
  const clientNames = clients?.map((client) => client.name).join(", ")
  const title =
    type === "teams"
      ? teamNames || t("profile.DriverPayCard.noTeam")
      : clientNames || t("profile.DriverPayCard.noClient")

  return (
    <Card
      styles={{ body: { padding: "0.5em" } }}
      className="text-sm border-0 w-auto"
      extra={
        <div className="flex space-x-2">
          <DriverPayFormModal
            button={
              <IconContainer onClick={() => {}}>
                <FontAwesomeIcon className="text-gray-400" icon={faPen} />
              </IconContainer>
            }
            config={config}
            type={type}
          />
          <DriverPayDeleteModal configId={config.id} type={type} />
        </div>
      }
      title={title}
      style={{ minWidth: 300, maxWidth: 600 }}
    >
      <Descriptions bordered column={1}>
        {!!basePrice && (
          <Descriptions.Item label={t("profile.DriverPaySettings.basePrice")}>
            <DisplayPrice amount={basePrice} currencyCode={currency.code} justify="start" />
          </Descriptions.Item>
        )}
        {!!perDelivery && (
          <Descriptions.Item label={t("profile.DriverPaySettings.perDelivery")}>
            <DisplayPrice amount={perDelivery} currencyCode={currency.code} justify="start" />
          </Descriptions.Item>
        )}
        {!!perKm && preference.distanceUnit === DistanceUnit.KM && (
          <Descriptions.Item label={t("profile.DriverPaySettings.perKm")}>
            <DisplayPrice amount={perKm} currencyCode={currency.code} justify="start" />
          </Descriptions.Item>
        )}
        {!!minKm && preference.distanceUnit === DistanceUnit.KM && (
          <Descriptions.Item label={t("profile.DriverPaySettings.minKm")}>
            {minKm}
          </Descriptions.Item>
        )}
        {!!perMi && preference.distanceUnit === DistanceUnit.MI && (
          <Descriptions.Item label={t("profile.DriverPaySettings.perMi")}>
            <DisplayPrice amount={perMi} currencyCode={currency.code} justify="start" />
          </Descriptions.Item>
        )}
        {!!minMi && preference.distanceUnit === DistanceUnit.MI && (
          <Descriptions.Item label={t("profile.DriverPaySettings.minMi")}>
            {minMi}
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  )
}

export default DriverPayCard
